import { useEffect, useRef, useState, type ReactNode } from 'react';

import LucideSymbolIcon from '../icons/LucideSymbolIcon';

type BibliothequeMeta = {
  auteurBibliotheque?: string | null;
  categorieBibliotheque?: string | null;
  langueBibliotheque?: string | null;
  tagsBibliotheque?: unknown;
  anneePublication?: number | string | null;
  description?: string | null;
};

type Props = {
  fileUrl?: string | null;
  title: string;
  kind?: 'pdf' | 'image';
  loading?: boolean;
  bibliotheque?: BibliothequeMeta | null;
};

const PDFJS_VERSION = '4.10.38';

export function isElearningPdfStorageKey(storageKey?: string | null) {
  return String(storageKey ?? '').toLowerCase().endsWith('.pdf');
}

export function isElearningImageStorageKey(storageKey?: string | null) {
  return /\.(jpg|jpeg|png|webp)$/i.test(String(storageKey ?? ''));
}

export function formatDocumentKindLabel(kind: 'pdf' | 'image') {
  return kind === 'pdf' ? 'Document PDF' : 'Image';
}

function normalizeTags(tags: unknown) {
  if (!Array.isArray(tags)) return [];
  return tags.map((tag) => String(tag).trim()).filter(Boolean);
}

function formatLangueLabel(code?: string | null) {
  if (!code) return '';
  const labels: Record<string, string> = {
    fr: 'Français',
    en: 'Anglais',
    es: 'Espagnol',
    de: 'Allemand',
    it: 'Italien',
    pt: 'Portugais',
    ar: 'Arabe',
    zh: 'Chinois',
  };
  return labels[code.toLowerCase()] ?? code.toUpperCase();
}

export function formatBibliothequeMetaLine(meta: BibliothequeMeta) {
  return [
    meta.auteurBibliotheque,
    meta.categorieBibliotheque,
    meta.langueBibliotheque ? formatLangueLabel(meta.langueBibliotheque) : null,
    meta.anneePublication ? String(meta.anneePublication) : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

function blockCopyEvent(event: React.SyntheticEvent) {
  event.preventDefault();
}

type PdfPage = {
  getViewport: (params: { scale: number }) => { width: number; height: number };
  render: (params: { canvasContext: CanvasRenderingContext2D; viewport: unknown }) => { promise: Promise<void> };
};

type PdfDocument = {
  numPages: number;
  getPage: (pageNumber: number) => Promise<PdfPage>;
};

type PdfJsModule = {
  GlobalWorkerOptions: { workerSrc: string };
  getDocument: (params: { url: string; withCredentials?: boolean }) => { promise: Promise<PdfDocument> };
};

let pdfJsLoader: Promise<PdfJsModule> | null = null;

function loadPdfJs(): Promise<PdfJsModule> {
  if (!pdfJsLoader) {
    pdfJsLoader = import(
      /* webpackIgnore: true */
      `https://cdn.jsdelivr.net/npm/pdfjs-dist@${PDFJS_VERSION}/build/pdf.min.mjs`
    ).then((mod) => {
      const pdfjsLib = mod as PdfJsModule;
      pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${PDFJS_VERSION}/build/pdf.worker.min.mjs`;
      return pdfjsLib;
    });
  }
  return pdfJsLoader;
}

export function ElearningDocumentCardShell({
  title,
  meta,
  description,
  chips,
  notice,
  children,
}: {
  title: string;
  meta: string;
  description?: string | null;
  chips?: string[];
  notice?: string;
  children: ReactNode;
}) {
  return (
    <section className="elearning-document-card" aria-label={title}>
      <div className="elearning-document-card__accent" aria-hidden />
      <header className="elearning-document-card__header">
        <span className="elearning-document-card__icon" aria-hidden>
          <LucideSymbolIcon name="library_big" tone="inherit" size={22} />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="elearning-document-card__title">{title}</h2>
          <p className="elearning-document-card__meta">{meta}</p>
          {description ? <p className="elearning-document-card__description">{description}</p> : null}
          {chips && chips.length > 0 ? (
            <div className="elearning-document-card__chips" aria-label="Mots-clés">
              {chips.map((chip) => (
                <span key={chip} className="elearning-document-card__chip">
                  {chip}
                </span>
              ))}
            </div>
          ) : null}
        </div>
      </header>
      {notice ? <p className="elearning-document-card__notice">{notice}</p> : null}
      <div className="elearning-document-card__stage">{children}</div>
    </section>
  );
}

function DocumentStageMessage({ icon, children }: { icon: 'hourglass_empty' | 'error'; children: ReactNode }) {
  return (
    <div className="elearning-document-card__state">
      <LucideSymbolIcon name={icon} tone="muted" size={28} />
      <p>{children}</p>
    </div>
  );
}

function PdfDocumentStage({ fileUrl, title }: { fileUrl: string; title: string }) {
  const pdfHostRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!pdfHostRef.current) return undefined;

    let cancelled = false;
    const host = pdfHostRef.current;
    host.innerHTML = '';
    setLoading(true);
    setError('');

    (async () => {
      const pdfjsLib = await loadPdfJs();
      if (cancelled) return;

      const pdf = await pdfjsLib.getDocument({ url: fileUrl, withCredentials: true }).promise;
      if (cancelled) return;

      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum += 1) {
        const page = await pdf.getPage(pageNum);
        if (cancelled) return;
        const viewport = page.getViewport({ scale: 1.25 });
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        if (!context) continue;
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        canvas.className = 'elearning-document-card__page';
        canvas.draggable = false;
        canvas.oncontextmenu = (event) => event.preventDefault();
        await page.render({ canvasContext: context, viewport }).promise;
        host.appendChild(canvas);
      }
    })()
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Lecture PDF impossible');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [fileUrl]);

  if (loading) {
    return <DocumentStageMessage icon="hourglass_empty">Chargement du document…</DocumentStageMessage>;
  }

  if (error) {
    return <DocumentStageMessage icon="error">{error}</DocumentStageMessage>;
  }

  return (
    <div
      ref={pdfHostRef}
      className="elearning-document-card__pdf elearning-readonly"
      onContextMenu={blockCopyEvent}
      onCopy={blockCopyEvent}
      onCut={blockCopyEvent}
      aria-label={title}
    />
  );
}

function ImageDocumentStage({ fileUrl, title }: { fileUrl: string; title: string }) {
  return (
    <div
      className="elearning-document-card__image-wrap elearning-readonly"
      onContextMenu={blockCopyEvent}
      onCopy={blockCopyEvent}
      onCut={blockCopyEvent}
      onDragStart={blockCopyEvent}
    >
      <img src={fileUrl} alt={title} draggable={false} className="elearning-document-card__image" />
    </div>
  );
}

export default function ElearningDocumentViewer({
  fileUrl,
  title,
  kind = 'pdf',
  loading = false,
  bibliotheque,
}: Props) {
  const metaLine = bibliotheque ? formatBibliothequeMetaLine(bibliotheque) : '';
  const meta = metaLine || formatDocumentKindLabel(kind);
  const chips = bibliotheque ? normalizeTags(bibliotheque.tagsBibliotheque) : [];
  const description = bibliotheque?.description?.trim() || null;

  return (
    <ElearningDocumentCardShell
      title={title}
      meta={meta}
      description={description}
      chips={chips}
      notice="Lecture seule — copie et téléchargement désactivés"
    >
      {loading || !fileUrl ? (
        <DocumentStageMessage icon="hourglass_empty">Chargement du document…</DocumentStageMessage>
      ) : kind === 'image' ? (
        <ImageDocumentStage fileUrl={fileUrl} title={title} />
      ) : (
        <PdfDocumentStage fileUrl={fileUrl} title={title} />
      )}
    </ElearningDocumentCardShell>
  );
}
